/**
 * ENG-4911 — pure helper for the integration-change hash used by the
 * `/host/refresh` reconciliation loop in manager-worker.
 *
 * Originally the hash covered only `definition_id + credentials`, which
 * meant a config-only change in the database (e.g. switching
 * `xero_tenant_id` from one tenant to another without rotating the
 * access token) silently failed to propagate: the hash matched the
 * stored value, the manager short-circuited before `writeIntegrations`,
 * `.env.integrations` was never rewritten, and the running MCP child
 * kept calling Xero with the old tenant id. This was a real
 * cross-tenant data exposure path on Sterling 2026-05-10.
 *
 * The fix is to also fold `config` into the hash payload. We use a
 * stable, ordered representation (sorted keys) so that two semantically
 * equivalent config blobs hash identically — without that, JSON's
 * insertion-order serialization could spuriously bump the hash on every
 * poll.
 */

import { createHash } from 'node:crypto';

interface HashableIntegration {
  definition_id: string;
  credentials?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      out[key] = canonicalize(obj[key]);
    }
    return out;
  }
  return value;
}

export function computeIntegrationsHash(integrations: HashableIntegration[]): string {
  const payload = integrations.map((i) =>
    `${i.definition_id}:${JSON.stringify(canonicalize(i.credentials ?? {}))}:${JSON.stringify(canonicalize(i.config ?? {}))}`,
  );
  return createHash('sha256')
    .update(JSON.stringify(payload))
    .digest('hex')
    .slice(0, 16);
}
